import type { NoteName } from "../guitarEngine";
import type {
  AttackFinger,
  OpenString,
  RhythmPathMission,
  RhythmPlayPattern,
  RhythmSubMission,
  StrumDirection,
  StrumGesture,
} from "./rhythmPathPlan";
import { expandRhythmHits, resolveStepBpm } from "./rhythmPathPlan";

export type ExpectedHit = {
  index: number;
  tMs: number;
  /** Offset en beats desde el inicio (para timeline / compases). */
  beatOffset: number;
  /** Duración del evento (nota o silencio) en ms. */
  durationMs: number;
  durationBeats: number;
  finger: AttackFinger | null;
  openString: OpenString;
  pitchLabel: string | null;
  note: NoteName;
  octave: number;
  midi: number;
  isRest: boolean;
  direction: StrumDirection | null;
  gesture: StrumGesture | null;
};

export type DetectedAttack = {
  tMs: number;
  volume: number;
  note?: NoteName | null;
  octave?: number;
  midi?: number;
};

export type HitScore = {
  expectedIndex: number;
  deltaMs: number | null;
  hit: boolean;
  volume: number | null;
  pitchOk: boolean;
};

export function buildExpectedHits(
  pattern: RhythmPlayPattern,
  bpm: number
): ExpectedHit[] {
  const msPerBeat = 60_000 / bpm;
  return expandRhythmHits(pattern).map((h, index) => ({
    index,
    tMs: h.beatOffset * msPerBeat,
    beatOffset: h.beatOffset,
    durationMs: h.durationBeats * msPerBeat,
    durationBeats: h.durationBeats,
    finger: h.finger,
    openString: h.openString,
    pitchLabel: h.pitchLabel,
    note: h.note,
    octave: h.octave,
    midi: h.midi,
    isRest: h.isRest,
    direction: h.direction,
    gesture: h.gesture,
  }));
}

export function sessionDurationMs(expected: ExpectedHit[], bpm: number): number {
  if (!expected.length) return 0;
  const last = expected[expected.length - 1];
  const trail = (60_000 / bpm) * 0.35;
  return last.tMs + last.durationMs + trail;
}

export function buildStepExpectedHits(
  mission: RhythmPathMission,
  step: RhythmSubMission,
  bpmOverride?: number
): {
  expected: ExpectedHit[];
  bpm: number;
  toleranceMs: number;
  successRatio: number;
} {
  const bpm = resolveStepBpm(mission, step, bpmOverride);
  return {
    expected: buildExpectedHits(step, bpm),
    bpm,
    toleranceMs: step.toleranceMs ?? mission.toleranceMs,
    successRatio: step.successRatio ?? mission.successRatio,
  };
}

/**
 * Índice del evento activo: ventana [t, t+duration).
 * lookAheadMs bajo = el dedo cambia alineado al onset (sync con el tracker).
 */
export function getActiveExpectedHitIndex(
  expected: ExpectedHit[],
  tMs: number,
  lookAheadMs = 25
): number {
  if (!expected.length) return -1;

  for (let i = 0; i < expected.length; i++) {
    const e = expected[i];
    const end = e.tMs + Math.max(e.durationMs, 1);
    if (tMs >= e.tMs - lookAheadMs && tMs < end) return i;
  }

  let best = 0;
  let bestScore = Infinity;
  for (let i = 0; i < expected.length; i++) {
    const d = expected[i].tMs - tMs;
    const score = d >= -lookAheadMs ? Math.abs(d) : Math.abs(d) + 40;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

function pitchMatchesExpected(
  attack: DetectedAttack,
  exp: ExpectedHit,
  midiTol = 1
): boolean {
  if (attack.midi != null && Number.isFinite(attack.midi) && attack.midi > 0) {
    return Math.abs(Math.round(attack.midi) - exp.midi) <= midiTol;
  }
  if (attack.note && attack.octave != null) {
    return attack.note === exp.note && attack.octave === exp.octave;
  }
  return false;
}

/** Ataques dentro de ventanas de silencio (penaliza tocar en rest). */
export function countRestViolations(
  expected: ExpectedHit[],
  attacks: DetectedAttack[],
  toleranceMs: number
): number {
  const rests = expected.filter((e) => e.isRest);
  if (!rests.length || !attacks.length) return 0;

  let violations = 0;
  for (const rest of rests) {
    // Margen interior: evita castigar el ring-out de la nota anterior.
    const pad = Math.min(toleranceMs * 0.5, rest.durationMs * 0.2);
    const start = rest.tMs + pad;
    const end = rest.tMs + rest.durationMs - pad * 0.25;
    if (end <= start) continue;

    const hitInRest = attacks.some((a) => a.tMs >= start && a.tMs <= end);
    if (hitInRest) violations += 1;
  }
  return violations;
}

export function scoreRhythmSession(
  expected: ExpectedHit[],
  attacks: DetectedAttack[],
  toleranceMs: number,
  requirePitch = false
): HitScore[] {
  const usedAttacks = new Set<number>();
  const noteEvents = expected.filter((e) => !e.isRest);

  return noteEvents.map((exp) => {
    let bestIdx = -1;
    let bestDist = Infinity;
    let bestPitchOk = false;

    for (let i = 0; i < attacks.length; i++) {
      if (usedAttacks.has(i)) continue;
      const d = Math.abs(attacks[i].tMs - exp.tMs);
      if (d > toleranceMs) continue;
      const pitchOk = !requirePitch || pitchMatchesExpected(attacks[i], exp);
      const better =
        bestIdx < 0 ||
        (pitchOk && !bestPitchOk) ||
        (pitchOk === bestPitchOk && d < bestDist);
      if (better) {
        bestIdx = i;
        bestDist = d;
        bestPitchOk = pitchOk;
      }
    }

    if (bestIdx < 0) {
      return {
        expectedIndex: exp.index,
        deltaMs: null,
        hit: false,
        volume: null,
        pitchOk: false,
      };
    }

    usedAttacks.add(bestIdx);
    const atk = attacks[bestIdx];
    const hit = requirePitch ? bestPitchOk : true;
    return {
      expectedIndex: exp.index,
      deltaMs: atk.tMs - exp.tMs,
      hit,
      volume: atk.volume,
      pitchOk: bestPitchOk,
    };
  });
}

export function evaluateRhythmSuccess(
  successRatio: number,
  expected: ExpectedHit[],
  scores: HitScore[],
  attacks: DetectedAttack[] = [],
  toleranceMs = 150
): {
  passed: boolean;
  hitCount: number;
  total: number;
  pitchMisses: number;
  restViolations: number;
} {
  const hitCount = scores.filter((s) => s.hit).length;
  const pitchMisses = scores.filter((s) => s.deltaMs != null && !s.pitchOk).length;
  const total = expected.filter((e) => !e.isRest).length;
  const ratioOk = total > 0 && hitCount / total >= successRatio;
  const restViolations = countRestViolations(expected, attacks, toleranceMs);
  return {
    passed: ratioOk && restViolations === 0,
    hitCount,
    total,
    pitchMisses,
    restViolations,
  };
}

export function createVolumeOnsetTracker(opts?: {
  absFloor?: number;
  delta?: number;
  minGapMs?: number;
}) {
  const absFloor = opts?.absFloor ?? 0.005;
  const delta = opts?.delta ?? 0.008;
  const minGapMs = opts?.minGapMs ?? 85;
  let prev = 0;
  let noiseFloor = 0.003;
  let lastAttackAt = -Infinity;

  return {
    feed(volume: number, tMs: number): DetectedAttack | null {
      const v = Math.max(0, volume);
      if (v < Math.max(noiseFloor * 2, absFloor)) {
        noiseFloor = noiseFloor * 0.92 + v * 0.08;
      }
      const floor = Math.max(absFloor, noiseFloor * 2.2);
      const jump = v - prev;
      const rising = jump >= delta && v >= floor;
      const spaced = tMs - lastAttackAt >= minGapMs;

      prev = v > prev ? v : prev * 0.82 + v * 0.18;

      if (rising && spaced) {
        lastAttackAt = tMs;
        return { tMs, volume: v };
      }
      return null;
    },
    reset() {
      prev = 0;
      noiseFloor = 0.003;
      lastAttackAt = -Infinity;
    },
  };
}
